/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Autocomplete } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './Modals.module.css';
import { useTranslation } from 'react-i18next';
import { Drug, getDrugs, getHormones, Hormone, OptionType } from './helper.stim';
import { PaginatedResponse } from '../../types';

interface CreateHormoneModalProps {
  hormoneModalOpen: boolean;
  handleHormoneModalClose: () => void;
  handleSubmitClose: (id: string, name: string) => void;
}

const CreateHormoneModal: React.FC<CreateHormoneModalProps> = ({
  hormoneModalOpen,
  handleHormoneModalClose,
  handleSubmitClose,
}) => {
  const { t } = useTranslation('stimulation_config');
  const [meds, setMeds] = useState<OptionType[]>([]);
  const [selectedHormone, setSelectedHormone] = useState<OptionType | null>(null);

  useEffect(() => {
    getHormones()
      .then((hormones: Hormone[]) => {
        const drugOptions: OptionType[] = hormones.map((hormone: Hormone) => ({
          label: hormone.name,
          value: hormone.id.toString(),
          unit: hormone.unit || '',
        }));
        setMeds(drugOptions);
      });
  }, []);

  // Formik validation schema
  const validationSchema = Yup.object({
    hormoneName: Yup.string().required(t('HormoneNameIsRequired')),
  });

  const formik = useFormik({
    initialValues: {
      hormoneName: '',
    },
    validationSchema,
    onSubmit: () => {
      if (selectedHormone) {
        handleSubmitClose(selectedHormone.value, selectedHormone.label);
        handleHormoneModalClose(); // Close the modal after submission
      }
    },
  });

  return (
    <Modal open={hormoneModalOpen} onClose={handleHormoneModalClose}>
      <Box className={styles.modal}>
        <Box className={styles.modalHeader}>
          <Typography variant="h3" gutterBottom>
            {t('AddHormone')}
          </Typography>
        </Box>
        <form onSubmit={formik.handleSubmit} className={styles.modalBody}>
          <Autocomplete
            options={meds}
            getOptionLabel={(option) => option.label}
            onChange={(event, value) => {
              setSelectedHormone(value);
              formik.setFieldValue('hormoneName', value?.label || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('HormoneNameLabel')}
                name="hormoneName"
                error={formik.touched.hormoneName && Boolean(formik.errors.hormoneName)}
                helperText={formik.touched.hormoneName && formik.errors.hormoneName}
                margin="normal"
              />
            )}
          />
          {/* Read-only unit field */}
          <TextField
            label={t('Unit')}
            value={selectedHormone?.unit || ''}
            margin="normal"
            InputProps={{ readOnly: true }}
            fullWidth
            style={{ marginTop: 8 }}
          />
          <Box className={styles.modalActions} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
            <Button
              variant="outlined"
              onClick={handleHormoneModalClose}
              className={styles.modalCancelBtn}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={styles.modalSaveBtn}
              disabled={!selectedHormone}
            >
              {t('Submit')}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateHormoneModal;